import { Zap } from 'lucide-react'
import type { AggregatorProject } from '../../types'
import { useOrgType } from '../../contexts/OrgTypeContext'

/**
 * Inbound triage panel — "Today's inbound, scored".
 *
 * Shows inbound projects (review_status === 'pending') ranked by their blind
 * match score against the rest of the book. The score comes from the
 * cross-pollination match run (top_match_score on the project record); the
 * match is computed on privacy-preserving fingerprints, never the documents
 * themselves, so the panel can rank inbound before anyone opens a deck.
 */

interface InboundTriagePanelProps {
  projects: AggregatorProject[]
  onProjectClick?: (projectId: string) => void
}

function scoreColor(score: number): string {
  if (score >= 0.8) return 'bg-emerald-500'
  if (score >= 0.6) return 'bg-iflow-500'
  if (score >= 0.4) return 'bg-amber-400'
  return 'bg-gray-300'
}

function scoreLabel(score: number | null | undefined): string {
  if (score === null || score === undefined) return '—'
  return `${Math.round(score * 100)}%`
}

export default function InboundTriagePanel({ projects, onProjectClick }: InboundTriagePanelProps) {
  const { vocab } = useOrgType()
  const inbound = projects
    .filter((p) => p.review_status === 'pending')
    .sort((a, b) => (b.top_match_score ?? -1) - (a.top_match_score ?? -1))

  if (inbound.length === 0) return null

  return (
    <div
      className="mb-6 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
      data-testid="inbound-triage-panel"
    >
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Zap size={16} className="text-iflow-500" />
            {vocab.inboundTitle}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {vocab.inboundSubtitle}
          </p>
        </div>
        <span
          className="text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1"
          data-testid="inbound-count"
        >
          {inbound.length} inbound
        </span>
      </div>

      <ul className="divide-y divide-gray-50">
        {inbound.map((p) => {
          const score = p.top_match_score ?? null
          const pct = score !== null ? Math.round(score * 100) : 0
          return (
            <li
              key={p.project_id}
              data-testid={`inbound-row-${p.project_id}`}
              className={`px-5 py-3 flex items-center gap-4 ${
                onProjectClick ? 'cursor-pointer hover:bg-gray-50' : ''
              }`}
              onClick={() => onProjectClick?.(p.project_id)}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                <p className="text-xs text-gray-500 truncate">{p.entity_name}</p>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                {typeof p.match_count === 'number' && p.match_count > 0 && (
                  <span className="text-xs text-gray-500">
                    {p.match_count} match{p.match_count === 1 ? '' : 'es'}
                  </span>
                )}
                {/* Bug #5898 (F18): a null score collapsed to a 0%-width bar
                    plus a bare "—", which read as a blank row with no
                    percentage, bar, or label at all. Skip the bar and say so
                    in words instead, same fix as InboundTriageTable. */}
                {score === null ? (
                  <span
                    className="text-xs font-medium text-gray-400 w-11 text-right"
                    data-testid={`inbound-score-${p.project_id}`}
                  >
                    Not scored
                  </span>
                ) : (
                  <>
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${scoreColor(score)}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span
                      className="text-sm font-semibold text-gray-800 w-11 text-right tabular-nums"
                      data-testid={`inbound-score-${p.project_id}`}
                    >
                      {scoreLabel(score)}
                    </span>
                  </>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
